import { useCallback, useRef } from 'react'

const POPUP_POLL_INTERVAL_MS = 500

export interface UsePopupClosePollReturn {
  startPolling: (popup: Window) => void
  stopPolling: () => void
}

/**
 * Polls a popup window reference to detect manual closure by the user.
 * Calls onClose when the popup is detected as closed.
 */
export const usePopupClosePoll = (onClose: () => void): UsePopupClosePollReturn => {
  const popupRef = useRef<Window | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const startPolling = useCallback(
    (popup: Window) => {
      stopPolling()
      popupRef.current = popup
      timerRef.current = setInterval(() => {
        if (popupRef.current?.closed) {
          stopPolling()
          onClose()
        }
      }, POPUP_POLL_INTERVAL_MS)
    },
    [stopPolling, onClose]
  )

  return { startPolling, stopPolling }
}
